import type { ReactNode } from 'react';

/**
 * The one surface everything in the portal is drawn on.
 *
 * **It exists so there is a single answer to "what does a panel look like here".** The portal grew
 * three: a `rounded-2xl` border for some sections, a `rounded-xl` one for list items, and bare text
 * on the page background for others, so a screen made of all three read as three screens. The
 * radius, the border, the ground and the shadow are decided once, and dark is part of that decision
 * rather than something each caller remembers.
 *
 * `as` is here for the one thing a wrapper cannot fake: a list item has to be an `<li>` and a
 * landmark has to be a `<section>`, and a `<div>` with the right classes is neither.
 */
export function Card({
    as: Element = 'div',
    interactive = false,
    className = '',
    children
}: {
    as?: 'div' | 'section' | 'li' | 'article';
    /** Lifts on hover. Only for a card that is itself a link or a button — never for decoration. */
    interactive?: boolean;
    className?: string;
    children: ReactNode;
}) {
    return (
        <Element
            className={`rounded-2xl border border-slate-200/80 bg-white shadow-sm shadow-slate-900/[0.03] dark:border-slate-800 dark:bg-slate-900 dark:shadow-none ${
                interactive
                    ? 'transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md hover:shadow-brand-900/5 dark:hover:border-brand-700'
                    : ''
            } ${className}`}
        >
            {children}
        </Element>
    );
}
